import { Menu } from 'components/Menu/Menu'
import { Item } from 'components/Menu/MenuItem'
import { ChevronRightSmall, Edit1, Trash2, UserProfileCircle } from 'blaze-ui/icons'
import { Key, useMemo } from 'react'
import { generatePath, useHistory, useLocation } from 'react-router-dom'
import { BRAND_KITS_PATH } from 'paths'
import { useConfirmDelete } from 'hooks/useConfirmDelete'
import { useDeleteBrandKit } from 'hooks/queries/brandKits/useDeleteBrandKit'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { BrandKit } from 'types'
import { MenuProps } from 'components/Menu/Types'
import { mergeProps } from '@react-aria/utils'
import { useRootStore } from 'stores/RootStore'
import { CollectionElement } from '@react-types/shared/src/collections'
import { LinkedAccountsDrawer } from 'screens/BrandKit/LinkedAccountsDrawer/LinkedAccountsDrawer'
import { LinkedAccountsModal } from 'screens/BrandKit/LinkedAccountsDrawer/LinkedAccountsModal'
import { Panel } from 'screens/BrandKit/LinkedAccountsDrawer/consts'
import { useBrandKitSources } from 'hooks/queries/useBrandKitSources'
import { collectResults } from 'utils/query'
import { Counter } from 'blaze-ui/Counter/Counter'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import Styles from 'screens/BrandKit/BrandKitMenu/BrandKitMenu.module.scss'
import { useModals } from 'almanac-ui/Modal'

interface Props extends Partial<Pick<MenuProps<any>, 'onAction' | 'menuType'>> {
  brandKit: BrandKit
  children?: (collection: CollectionElement<any>[]) => CollectionElement<any> | CollectionElement<any>[]
  /** Called when Rename is chosen (e.g. close a parent drawer on mobile). */
  onRename?: () => void
}

export const BrandKitMenu = ({ brandKit, children = (collection) => collection, onRename, ...rest }: Props) => {
  const workspaceId = useCurrentWorkspaceId()
  const { pathname, hash, state, search } = useLocation<{ isEditingName: boolean; brandKitId: number } | undefined>()
  const history = useHistory()
  const { drawersStore } = useRootStore()
  const { closeAllDrawers, openDrawer } = drawersStore
  const { openModal } = useModals()
  const { isMobile } = useResponsiveDesign()

  const linkedAccountsQuery = useBrandKitSources(
    { brandKitId: brandKit.id, sourceType: ['ExternalIntegration'] },
    { cacheTime: 1000 * 60 * 5 },
  )
  const linkedAccounts = collectResults(linkedAccountsQuery, (data) => data.brand_kit_sources)

  const linkedAccountPanel = useMemo(() => {
    if (linkedAccounts.length > 0) {
      return Panel.ManageAccounts
    }

    return Panel.SelectAccounts
  }, [linkedAccounts])

  const goToBrandKits = () => {
    history.push(generatePath(BRAND_KITS_PATH, { workspaceId }))
  }

  const confirmDelete = useConfirmDelete()

  const { mutate: deleteBrandKit, isLoading: isDeleting } = useDeleteBrandKit(
    { id: brandKit.id },
    {
      onError: () => {
        showErrorMessageToast('There was a problem deleting your Brand Kit. Our team has been notified.')
      },
      onSuccess: () => {
        showToastMessage('Brand Kit successfully deleted.', 'success')
        goToBrandKits()
      },
    },
  )

  const handleMenuAction = async (key: Key) => {
    if (isDeleting) {
      return
    }

    switch (key) {
      case 'linked_accounts':
        if (isMobile) {
          openDrawer(LinkedAccountsDrawer, { brandKitId: brandKit.id, initialPanel: linkedAccountPanel })
        } else {
          openModal(LinkedAccountsModal, { brandKitId: brandKit.id, initialPanel: linkedAccountPanel })
        }
        return
      case 'delete':
        const confirmed = await confirmDelete({
          msg: 'You are about to delete this Brand Kit',
          confirmText: 'Delete Brand Kit',
        })
        if (confirmed) {
          closeAllDrawers()
          deleteBrandKit()
        }
        return
      case 'rename':
        onRename?.()
        history.replace({
          pathname,
          hash,
          search,
          state: { ...state, isEditingName: true, brandKitId: brandKit.id },
        })
        return
    }
  }

  return (
    <Menu {...mergeProps({ onAction: handleMenuAction }, rest)} aria-label="Brand Kit Actions">
      {children([
        <Item
          key="linked_accounts"
          textValue="Linked Accounts"
          frontIcon={UserProfileCircle}
          endIcon={rest.menuType === 'tray' ? ChevronRightSmall : undefined}
          description={
            rest.menuType === 'tray'
              ? 'Connecting social media accounts helps Blaze create better on-brand content.'
              : undefined
          }
        >
          Linked Accounts
          <span className={Styles.counterBeforeChevron}>
            <Counter count={linkedAccounts.length} />
          </span>
        </Item>,
        <Item
          key="delete"
          textValue="Delete"
          frontIcon={Trash2}
          endIcon={rest.menuType === 'tray' ? ChevronRightSmall : undefined}
        >
          Delete
        </Item>,
        <Item
          key="rename"
          textValue="Rename"
          frontIcon={Edit1}
          endIcon={rest.menuType === 'tray' ? ChevronRightSmall : undefined}
        >
          Rename
        </Item>,
      ])}
    </Menu>
  )
}
